import { useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, DragOverlay, PointerSensor, TouchSensor, useSensor, useSensors, closestCorners, type DragEndEvent, type DragOverEvent, type DragStartEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { apiGet, apiPatch, apiPut } from '../../lib/api';
import { useToast } from '../../contexts/ToastContext';
import { BoardColumn } from './BoardColumn';
import { BoardCardOverlay } from './BoardCard';
import { getColumns, columnKeysForTask, mutationForDrop, type BoardTask, type BoardCtx, type GroupBy } from '../../lib/boardGrouping';

interface BoardOrderRow { column_key: string; task_id: string; sort_order: number; }

/** Stable signature of the tasks' groupable fields — so we only resync local state when the
 *  data actually changes, not when the parent hands us a new-but-identical array each render.
 *  Without this, an incidental re-render (or a stale reload) clobbers an optimistic drop. */
function tasksSignature(tasks: BoardTask[]): string {
  return tasks.map(t => `${t.id}:${t.status}:${t.priority}:${t.assignee_id ?? ''}:${t.scope_ids.join('.')}:${t.due_date ?? ''}`).join('|');
}

export function TaskBoard({ tasks, ctx, groupBy, showEmpty, todayStr, onChanged, onTaskChanged, onQuickAdd, onOpenTask }: {
  tasks: BoardTask[]; ctx: BoardCtx; groupBy: GroupBy; showEmpty: boolean; todayStr: string;
  onChanged: () => void;
  // Optimistically patch the parent's task so it stays in sync WITHOUT a reload — otherwise the
  // parent keeps stale data and a later filter toggle re-syncs the board back to it, reverting moves (#210).
  onTaskChanged: (taskId: string, patch: Partial<BoardTask>) => void;
  onQuickAdd: (colKey: string, groupBy: GroupBy) => void;
  onOpenTask: (taskId: string) => void;
}) {
  const toast = useToast();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
  );
  const scopeColors = useMemo(
    () => new Map(ctx.scopes.map(s => [s.id, { name: s.name, color: s.color }])),
    [ctx.scopes],
  );

  // Local mirror of tasks — the board's source of truth during/after a drag, so an optimistic
  // move survives incidental re-renders and stale reloads (see tasksSignature above).
  const [localTasks, setLocalTasks] = useState<BoardTask[]>(tasks);
  const sig = tasksSignature(tasks);
  useEffect(() => { setLocalTasks(tasks); /* resync only when field values actually change */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  const [activeId, setActiveId] = useState<string | null>(null);
  // The dragged card's ORIGINAL column, captured at drag start. We can't read it from
  // active.data at drop time, because onDragOver re-renders the card into the hovered column and
  // updates its fromCol — which would make fromCol === toCol and skip the status PATCH (#210).
  const dragFromColRef = useRef<string | null>(null);
  const [orderMap, setOrderMap] = useState<Map<string, string[]>>(new Map());

  async function loadOrder() {
    try {
      const rows = await apiGet<BoardOrderRow[]>(`/tasks/board-order?group_by=${groupBy}`);
      const map = new Map<string, string[]>();
      for (const row of rows) {
        const list = map.get(row.column_key) ?? [];
        list.push(row.task_id);
        map.set(row.column_key, list);
      }
      setOrderMap(map);
    } catch {
      setOrderMap(new Map());
    }
  }
  useEffect(() => {
    loadOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy]);

  const columns = getColumns(groupBy, ctx);
  const byColumn = new Map<string, BoardTask[]>();
  for (const col of columns) byColumn.set(col.key, []);
  for (const t of localTasks) {
    for (const key of columnKeysForTask(t, groupBy, todayStr)) byColumn.get(key)?.push(t);
  }
  for (const col of columns) {
    const colTasks = byColumn.get(col.key) ?? [];
    const savedOrder = orderMap.get(col.key);
    if (savedOrder && savedOrder.length > 0) {
      const byId = new Map(colTasks.map(t => [t.id, t]));
      const ordered: BoardTask[] = [];
      for (const id of savedOrder) {
        const t = byId.get(id);
        if (t) { ordered.push(t); byId.delete(id); }
      }
      for (const t of colTasks) if (byId.has(t.id)) ordered.push(t);
      byColumn.set(col.key, ordered);
    }
  }
  const visibleColumns = showEmpty ? columns : columns.filter(c => (byColumn.get(c.key)?.length ?? 0) > 0);
  const activeTask = activeId ? localTasks.find(t => t.id === activeId) ?? null : null;

  function handleDragStart(e: DragStartEvent) {
    setActiveId(e.active.id as string);
    dragFromColRef.current = (e.active.data.current?.fromCol as string) ?? null;
  }

  // Live cross-column preview: as the card is dragged over a different column, move it there in
  // local state so it (dashed placeholder) appears in the target column immediately. Skipped for
  // 'project' grouping, where a task can belong to several columns at once (handled on drop).
  function handleDragOver(e: DragOverEvent) {
    if (groupBy === 'project') return;
    const { active, over } = e;
    if (!over) return;
    const task = localTasks.find(t => t.id === active.id);
    if (!task) return;
    const overCol = (over.data.current?.colKey ?? over.data.current?.fromCol) as string | undefined;
    if (!overCol) return;
    const currentCol = columnKeysForTask(task, groupBy, todayStr)[0];
    if (currentCol === overCol) return;
    const patch = mutationForDrop(task, groupBy, currentCol, overCol, todayStr);
    setLocalTasks(prev => prev.map(t => (t.id === task.id ? { ...t, ...patch } : t)));
  }

  async function handleDragEnd(e: DragEndEvent) {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const task = localTasks.find(t => t.id === active.id);
    if (!task) return;
    const fromCol = dragFromColRef.current; // ORIGINAL column (active.data.fromCol mutates during onDragOver)
    const toCol = (over.data.current?.colKey ?? over.data.current?.fromCol) as string | undefined;
    if (!toCol) return;

    const destIds = (byColumn.get(toCol) ?? []).map(t => t.id);
    const alreadyInDest = destIds.includes(task.id); // true for non-project (moved by onDragOver)

    let newIds: string[];
    if (alreadyInDest) {
      const oldIndex = destIds.indexOf(task.id);
      const overIndex = destIds.indexOf(over.id as string);
      newIds = (oldIndex !== -1 && overIndex !== -1 && oldIndex !== overIndex)
        ? arrayMove(destIds, oldIndex, overIndex) : destIds;
    } else {
      const without = destIds.filter(id => id !== task.id);
      const overIndex = without.indexOf(over.id as string);
      newIds = overIndex === -1 ? [...without, task.id] : [...without.slice(0, overIndex), task.id, ...without.slice(overIndex)];
    }

    setOrderMap(prev => { const next = new Map(prev); next.set(toCol, newIds); return next; });

    const changedColumn = fromCol !== toCol;
    let patch: Partial<BoardTask> | null = null;
    if (changedColumn) {
      patch = mutationForDrop(task, groupBy, fromCol, toCol, todayStr) as Partial<BoardTask>;
      // Ensure local state reflects the field change (no-op for non-project already moved by onDragOver).
      setLocalTasks(prev => prev.map(t => (t.id === task.id ? { ...t, ...patch } : t)));
      // Keep the parent in sync (no reload) so a later filter toggle doesn't revert the move.
      onTaskChanged(task.id, patch);
    }

    try {
      // Persist silently. We deliberately do NOT call onChanged() (a full parent reload) here:
      // the reload hands back a fresh array that clobbers this optimistic move, making the card
      // vanish from its new column until a manual refresh (#210). localTasks is the source of
      // truth after a drop; the parent resyncs on the next natural load (filter change / revisit).
      if (patch) await apiPatch(`/tasks/${task.id}`, patch);
      await apiPut('/tasks/board-order', { group_by: groupBy, column_key: toCol, task_ids: newIds });
    } catch {
      toast.error('Failed to move task');
      onChanged();
      await loadOrder();
    }
  }

  function handleDragCancel() {
    setActiveId(null);
    setLocalTasks(tasks); // revert any onDragOver preview
  }

  if (visibleColumns.length === 0) {
    return <div className="text-text-muted text-sm text-center py-16">No tasks match the current filter.</div>;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners}
      onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
      <div className="flex gap-3.5 overflow-x-auto h-full pb-2">
        {visibleColumns.map(col => (
          <BoardColumn key={col.key} col={col} tasks={byColumn.get(col.key) ?? []}
            scopeColors={scopeColors} onQuickAdd={k => onQuickAdd(k, groupBy)} onOpenTask={onOpenTask} />
        ))}
      </div>
      <DragOverlay>
        {activeTask ? <BoardCardOverlay task={activeTask} scopeColors={scopeColors} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
